import {useLocalStorage} from 'platform/components';
import {platformLightboxQueryParams} from 'platform/lightbox';

import {useState} from 'react';
// eslint-disable-next-line no-restricted-imports -- Clears all lightbox params in one replace.
import {useLocation, useSearchParams} from 'react-router-dom';

import {isNotNil} from 'ramda';

import type {AdvertisingPlatformCodeEnum} from '@dms/api/shared';

import {PREVIEW_PLATFORM_STORAGE_KEY} from '../components/AdvertisementPreview/constants/PREVIEW_PLATFORM_STORAGE_KEY';

const LIGHTBOX_QUERY_PARAMS = Object.values(platformLightboxQueryParams);

function withoutLightboxParams(pathname: string, search: string, hash: string) {
  const params = new URLSearchParams(search);
  LIGHTBOX_QUERY_PARAMS.forEach((param) => params.delete(param));
  return `${pathname}?${params.toString()}${hash}`;
}

// The lightbox's document listener handles Escape before the Dialog's window listener.
function isEscapeHandledByLightbox() {
  return (
    window.event instanceof KeyboardEvent &&
    window.event.key === 'Escape' &&
    window.event.defaultPrevented
  );
}

export function useAdvertisementPreviewDialog() {
  const [, setStoredPlatform] = useLocalStorage<string | undefined>(
    PREVIEW_PLATFORM_STORAGE_KEY,
    undefined
  );
  const [openedAtLocation, setOpenedAtLocation] = useState<string>();
  const [initialPlatformCode, setInitialPlatformCode] = useState<AdvertisingPlatformCodeEnum>();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const currentLocation = withoutLightboxParams(location.pathname, location.search, location.hash);
  const [previousLocation, setPreviousLocation] = useState(currentLocation);
  if (previousLocation !== currentLocation) {
    setPreviousLocation(currentLocation);
    setOpenedAtLocation(undefined);
  }
  const isOpen = openedAtLocation === currentLocation;

  const clearLightboxQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    if (!LIGHTBOX_QUERY_PARAMS.some((param) => params.has(param))) {
      return;
    }

    LIGHTBOX_QUERY_PARAMS.forEach((param) => params.delete(param));
    setSearchParams(params, {replace: true});
  };

  const open = (platformCode?: AdvertisingPlatformCodeEnum) => {
    clearLightboxQueryParams();
    if (isNotNil(platformCode)) {
      setStoredPlatform(platformCode);
    }
    setInitialPlatformCode(platformCode);
    setOpenedAtLocation(currentLocation);
  };

  const close = () => {
    if (isEscapeHandledByLightbox()) {
      return;
    }

    setOpenedAtLocation(undefined);
    clearLightboxQueryParams();
  };

  return {isOpen, initialPlatformCode, open, close};
}
